<template>
  <div class="musubi-page musubi-page--extforms">
    <header class="musubi-page__head">
      <h1>External forms</h1>
      <p class="text-muted">Capture leads from the forms already on your own website — WordPress or any site —
        straight into Yorlin, with the right GDPR treatment per form.</p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <div class="ef-grid">
      <!-- ── Keys ──────────────────────────────────────────────────────── -->
      <MusubiSectionCard title="Connection keys" subtitle="one server key, one public key" icon="lock">
        <p class="ef-lead">
          The <b>site key</b> is a secret for server-to-server use (the WordPress plugin). The
          <b>capture key</b> is public — it goes in the JS snippet below and can't hold a secret,
          so it carries stricter rate limits.
        </p>

        <div v-for="k in keys" :key="k.key_type" class="ef-key">
          <div class="ef-key__row">
            <div>
              <span class="ef-key__label">{{ k.key_type === 'site' ? 'Site key (secret)' : 'Capture key (public)' }}</span>
              <code v-if="k.present" class="ef-key__val">
                {{ k.key_type === 'capture' && k.value ? k.value : k.masked }}
              </code>
              <span v-else class="ef-key__none">not generated</span>
            </div>
            <div class="ef-key__actions">
              <button v-if="k.key_type === 'capture' && k.present && k.value" type="button" class="ef-btn ef-btn--ghost" @click="copyValue(k.value)">
                <CygnusIcon name="file" /> Copy
              </button>
              <button type="button" class="ef-btn" @click="rotate(k.key_type)">
                {{ k.present ? 'Rotate' : 'Generate' }}
              </button>
              <button v-if="k.present" type="button" class="ef-btn ef-btn--danger" @click="revoke(k.key_type)">Revoke</button>
            </div>
          </div>
          <p v-if="revealed[k.key_type]" class="ef-key__reveal">
            <CygnusIcon name="warning" />
            New {{ k.key_type }} key — copy it now, it won't be shown in full again:
            <code>{{ revealed[k.key_type] }}</code>
            <button type="button" class="ef-btn ef-btn--ghost" @click="copyValue(revealed[k.key_type])"><CygnusIcon name="file" /></button>
          </p>
        </div>
      </MusubiSectionCard>

      <!-- ── WordPress plugin ──────────────────────────────────────────── -->
      <MusubiSectionCard title="WordPress" subtitle="the easiest way in" icon="plug">
        <p class="ef-lead">
          Install the Yorlin Forms plugin, paste your <b>ingest endpoint</b> and <b>site key</b>,
          and your Contact Form 7 / Gravity / WPForms / Ninja submissions flow in automatically.
        </p>
        <div class="ef-code">
          <code>{{ captureEndpoint }}</code>
          <button type="button" class="ef-copy" @click="copyValue(captureEndpoint)"><CygnusIcon name="file" /> Copy endpoint</button>
        </div>
        <a class="ef-btn ef-btn--wide" :href="pluginDownload" download>
          <CygnusIcon name="download" /> Download plugin (.zip)
        </a>
      </MusubiSectionCard>
    </div>

    <!-- ── JS watcher snippet ──────────────────────────────────────────── -->
    <MusubiSectionCard title="Any website — JS form-watcher" subtitle="annotate the fields you want captured"
                       icon="code" class="ef-watcher">
      <p class="ef-lead">
        Not on WordPress? Add the pixel with your capture key, then mark the form with
        <code>data-yorlin-form</code> and tag the fields you want sent
        (<code>data-yorlin-field="email"</code>, <code>first_name</code>, <code>company</code>, …).
        Nothing else on the form is ever read.
      </p>
      <div class="ef-code ef-code--multi">
        <code>{{ watcherSnippet }}</code>
        <button type="button" class="ef-copy" @click="copyValue(watcherSnippet)"><CygnusIcon name="file" /> Copy</button>
      </div>
      <div class="ef-code ef-code--multi">
        <code>{{ exampleMarkup }}</code>
        <button type="button" class="ef-copy" @click="copyValue(exampleMarkup)"><CygnusIcon name="file" /> Copy</button>
      </div>
    </MusubiSectionCard>

    <!-- ── Connected forms ─────────────────────────────────────────────── -->
    <MusubiSectionCard title="Connected forms" subtitle="review + configure how each is treated" icon="list">
      <p v-if="!forms.length" class="text-muted small py-2">
        No forms yet. Once a submission arrives from your site it'll appear here to review.
      </p>
      <table v-else class="ef-table">
        <thead>
          <tr><th>Form</th><th>Source</th><th>Treatment</th><th>Consent field</th><th>Leads</th><th>Status</th></tr>
        </thead>
        <tbody>
          <tr v-for="f in forms" :key="f.id" :class="{ 'is-new': f.capture_status === 'new' }">
            <td>
              <b>{{ f.name }}</b>
              <span class="ef-ref">{{ f.external_ref }}</span>
            </td>
            <td class="ef-muted">{{ f.channel === 'site_key' ? 'WordPress' : 'JS watcher' }}</td>
            <td>
              <select :value="f.treatment" @change="update(f, 'treatment', $event.target.value)">
                <option value="enquiry">Enquiry (capture only)</option>
                <option value="signup">Signup (consent → marketing)</option>
              </select>
            </td>
            <td>
              <input v-if="f.treatment === 'signup'" type="text" class="ef-inp" placeholder="consent"
                     :value="f.consent_field || ''" @change="update(f, 'consent_field', $event.target.value)" />
              <span v-else class="ef-muted">—</span>
            </td>
            <td class="ef-num">{{ f.submissions }}</td>
            <td>
              <select :value="f.capture_status" @change="update(f, 'capture_status', $event.target.value)">
                <option value="new">New</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </td>
          </tr>
        </tbody>
      </table>
    </MusubiSectionCard>

    <div aria-live="polite" class="ef-copied" v-show="copiedNote">{{ copiedNote }}</div>
    <input ref="fallbackInput" class="ef-offscreen" tabindex="-1" aria-hidden="true" />
  </div>
</template>

<script>
/**
 * AdminExternalForms — external form-capture setup (visitor-id slice 1b).
 * Manages the per-tenant site/capture keys (generate/rotate/revoke, masked
 * display), lists auto-registered connected forms with a per-form GDPR
 * treatment + enable/disable, and serves the WP plugin download + JS watcher
 * snippet. Gated Ops::SETTINGS; available on all plan tiers.
 */
import useApi from "@apexl/icarus-core/src/services/api";

export default {
  name: 'AdminExternalForms',
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      keys: [],
      forms: [],
      captureEndpoint: '',
      watcherSnippet: '',
      pluginDownload: '/api/v1/admin/external-forms/plugin.zip',
      revealed: {},
      error: null,
      copiedNote: '',
      copyTimer: null,
    };
  },
  mounted() { this.load(); },
  computed: {
    exampleMarkup() {
      return '<form data-yorlin-form="contact" data-yorlin-name="Contact us">\n'
        + '  <input type="email" data-yorlin-field="email" name="email">\n'
        + '  <input type="text" data-yorlin-field="first_name" name="fname">\n'
        + '  <input type="text" data-yorlin-field="company" name="company">\n'
        + '  <button type="submit">Send</button>\n'
        + '</form>';
    },
  },
  methods: {
    async load() {
      try {
        const res = await this.api.get('/api/v1/admin/external-forms');
        const p = res?.data?.data ?? res?.data ?? res;
        this.keys = p?.keys || [];
        this.forms = p?.forms || [];
        this.captureEndpoint = p?.capture_endpoint || '';
        this.watcherSnippet = p?.watcher_snippet || '';
        this.pluginDownload = p?.plugin_download || this.pluginDownload;
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to load external forms.';
      }
    },
    async rotate(type) {
      try {
        const res = await this.api.post(`/api/v1/admin/external-forms/keys/${type}/rotate`, {});
        const p = res?.data?.data ?? res?.data ?? res;
        if (p?.value) { this.revealed = { ...this.revealed, [type]: p.value }; }
        await this.load();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to generate key.';
      }
    },
    async revoke(type) {
      try {
        await this.api.delete(`/api/v1/admin/external-forms/keys/${type}`);
        this.revealed = { ...this.revealed, [type]: '' };
        await this.load();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to revoke key.';
      }
    },
    async update(form, field, value) {
      try {
        await this.api.put(`/api/v1/admin/external-forms/${form.id}`, { [field]: value });
        form[field] = value;
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to update form.';
        await this.load();
      }
    },
    async copyValue(value) {
      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(value); copied = true; }
      } catch (e) { /* fall through */ }
      if (!copied) {
        const input = this.$refs.fallbackInput;
        if (input) { input.value = value; input.focus(); input.select(); try { document.execCommand('copy'); } catch (e) { /* ignore */ } }
      }
      this.copiedNote = 'Copied to clipboard.';
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => { this.copiedNote = ''; }, 1400);
    },
  },
};
</script>

<style scoped>
.ef-grid { display: grid; grid-template-columns: 1.3fr 1fr; gap: 16px; align-items: start; margin: 18px 0 16px; }
.ef-lead { color: var(--m-text-secondary); font-size: 0.9rem; line-height: 1.55; margin: 0 0 12px; }
.ef-lead b { color: var(--m-text-primary); }

.ef-key { border-top: 1px solid var(--m-border); padding: 12px 0; }
.ef-key:first-of-type { border-top: 0; }
.ef-key__row { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
.ef-key__label { display: block; font-size: 0.78rem; font-weight: 650; color: var(--m-text-secondary); }
.ef-key__val { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.82rem; color: var(--m-text-primary); word-break: break-all; }
.ef-key__none { font-size: 0.82rem; color: var(--m-text-tertiary); font-style: italic; }
.ef-key__actions { display: flex; gap: 6px; }
.ef-key__reveal { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; background: var(--m-warning-soft, rgba(234,179,8,0.1)); border-radius: 6px; padding: 8px 12px; font-size: 0.78rem; color: var(--m-text-secondary); margin: 8px 0 0; }
.ef-key__reveal i { color: var(--m-warning, #ca8a04); }
.ef-key__reveal code { font-size: 0.78rem; color: var(--m-text-primary); word-break: break-all; }

.ef-btn { background: var(--m-accent); color: #fff; border: 0; border-radius: 6px; padding: 6px 12px; font: inherit; font-size: 0.78rem; font-weight: 600; cursor: pointer; text-decoration: none; display: inline-flex; align-items: center; gap: 6px; }
.ef-btn:hover { filter: brightness(0.94); }
.ef-btn--ghost { background: transparent; color: var(--m-accent); border: 1px solid var(--m-border); }
.ef-btn--danger { background: transparent; color: var(--m-danger, #dc2626); border: 1px solid var(--m-border); }
.ef-btn--wide { justify-content: center; width: 100%; margin-top: 10px; padding: 9px 12px; }

.ef-code { position: relative; background: var(--m-surface-sunken, var(--m-bg)); border: 1px solid var(--m-border); border-radius: 8px; padding: 12px 96px 12px 14px; margin: 6px 0; }
.ef-code code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.78rem; color: var(--m-text-primary); white-space: pre-wrap; word-break: break-all; display: block; }
.ef-copy { position: absolute; top: 10px; right: 10px; background: var(--m-accent); color: #fff; border: 0; border-radius: 6px; padding: 6px 10px; font: inherit; font-size: 0.74rem; font-weight: 600; cursor: pointer; }

.ef-watcher { margin-bottom: 16px; }

.ef-table { width: 100%; border-collapse: collapse; font-size: 0.84rem; }
.ef-table th { text-align: left; font-size: 0.74rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-tertiary); padding: 8px 10px; border-bottom: 1px solid var(--m-border); }
.ef-table td { padding: 10px; border-bottom: 1px solid var(--m-border); vertical-align: middle; }
.ef-table tr.is-new { background: var(--m-warning-soft, rgba(234,179,8,0.07)); }
.ef-ref { display: block; font-family: ui-monospace, monospace; font-size: 0.72rem; color: var(--m-text-tertiary); margin-top: 2px; }
.ef-muted { color: var(--m-text-tertiary); }
.ef-num { text-align: center; font-weight: 650; }
.ef-table select, .ef-inp { padding: 6px 9px; border: 1px solid var(--m-border); border-radius: 6px; font: inherit; font-size: 0.8rem; background: var(--m-bg); color: var(--m-text-primary); }

.ef-copied { position: fixed; bottom: 22px; left: 50%; transform: translateX(-50%); background: var(--m-text-primary); color: var(--m-bg); padding: 8px 18px; border-radius: 999px; font-size: 0.82rem; font-weight: 600; z-index: 3000; box-shadow: 0 4px 16px rgba(0,0,0,0.2); }
.ef-offscreen { position: absolute; left: -9999px; width: 1px; height: 1px; }

@media (max-width: 1024px) { .ef-grid { grid-template-columns: 1fr; } }
</style>
